import { clientEnv } from '../buildTimeEnv';

/**
 * Chargify (Maxio) Configuration
 * 
 * Centralized configuration for Chargify payment processing.
 * To add a new site, simply add a new entry to the CHARGIFY_SITES object.
 * 
 * Usage:
 *   import { getChargifyConfig } from '../config/ChargifyConfig';
 *   const config = getChargifyConfig();
 */

/**
 * Chargify site configurations
 * Each site should have its own Chargify account with:
 * - publicKey: Chargify.js public API key (starts with 'chjs_')
 * - subdomain: Chargify subdomain (e.g., 'electricnow', 'chillfree')
 * 
 * To add a new site:
 * 1. Get the Chargify credentials from the backend team
 * 2. Add a new entry below with the site identifier
 * 3. The site identifier can be determined from window.location.hostname or environment variable
 */
const CHARGIFY_SITES = {
  // Electric Entertainment / ElectricNOW
  electricnow: {
    publicKey: clientEnv.REACT_APP_CHARGIFY_PUBLIC_KEY || '', // Set in .env
    subdomain: clientEnv.REACT_APP_CHARGIFY_SUBDOMAIN || 'electricnow',
    siteName: 'ElectricNOW',
  },
  
  // ChillFree TV
  chillfree: {
    publicKey: 'chjs_kkpj83fyfqd43xv7knx25p2k',
    subdomain: 'chillfree',
    siteName: 'ChillFree TV',
  },
  
  // Add new sites here following this pattern:
  // newsite: {
  //   publicKey: process.env.REACT_APP_CHARGIFY_PUBLIC_KEY || 'chjs_your_key_here',
  //   subdomain: 'newsite',
  //   siteName: 'New Site Name',
  // },
};

/**
 * Detect the current site based on hostname or environment variable
 * @returns {string} Site identifier (key from CHARGIFY_SITES)
 */
const detectSite = () => {
  // Check environment variable first (for explicit override)
  if (clientEnv.REACT_APP_SITE_ID) {
    return clientEnv.REACT_APP_SITE_ID.toLowerCase();
  }
  
  // Detect from hostname
  const hostname = window.location.hostname.toLowerCase();
  
  // Check for common patterns
  if (hostname.includes('electricnow') || hostname.includes('electric-entertainment')) {
    return 'electricnow';
  }
  
  if (hostname.includes('chillfree') || hostname.includes('chillfreetv')) {
    return 'chillfree';
  }
  
  // Default fallback (ChillFree is the default for this repo)
  return 'chillfree';
};

/**
 * Get Chargify configuration for the current site
 * @returns {Object} Chargify configuration object
 * @returns {string} .publicKey - Chargify.js public API key
 * @returns {string} .subdomain - Chargify subdomain
 * @returns {string} .serverHost - Full Chargify server host URL
 * @returns {string} .siteName - Display name of the site
 */
export const getChargifyConfig = () => {
  const siteId = detectSite();
  const siteConfig = CHARGIFY_SITES[siteId];
  
  if (!siteConfig) {
    console.warn(`⚠️ ChargifyConfig: No configuration found for site "${siteId}". Using default.`);
    // Fallback to first available config or chillfree
    return CHARGIFY_SITES.chillfree || Object.values(CHARGIFY_SITES)[0];
  }
  
  // Validate that we have required values
  if (!siteConfig.publicKey) {
    console.error(`❌ ChargifyConfig: Missing publicKey for site "${siteId}". Please set REACT_APP_CHARGIFY_PUBLIC_KEY in .env`);
  }
  
  if (!siteConfig.subdomain) {
    console.error(`❌ ChargifyConfig: Missing subdomain for site "${siteId}"`);
  }
  
  return {
    ...siteConfig,
    serverHost: `https://${siteConfig.subdomain}.chargify.com`,
  };
};

/**
 * Get all available site configurations (for debugging/admin)
 * @returns {Object} All site configurations
 */
export const getAllChargifyConfigs = () => {
  return Object.entries(CHARGIFY_SITES).reduce((acc, [key, config]) => {
    acc[key] = {
      ...config,
      serverHost: `https://${config.subdomain}.chargify.com`,
    };
    return acc;
  }, {});
};

/**
 * Check if Chargify is properly configured for the current site
 * @returns {boolean} True if configuration is valid
 */
export const isChargifyConfigured = () => {
  const config = getChargifyConfig();
  return !!(config.publicKey && config.subdomain);
};

export default {
  getChargifyConfig,
  getAllChargifyConfigs,
  isChargifyConfigured,
  detectSite,
};

